/**
 * Three.js Geometry Node Definitions
 *
 * Config-driven geometry declarations. Each node holds params and provides
 * buildGeometry() for SceneNode to call.
 * Also emits geometry on geometryOut for direct endpoint use (e.g. definitions).
 *
 * Two rendering paths:
 *   SceneNode path — SceneNode calls buildGeometry() and manages the mesh.
 *     animate() swaps mesh.geometry in-place (old geometry disposed).
 *   Endpoint path  — geometry emitted on geometryOut, flows to materials
 *     via ThreeEndpoint connections (used inside definitions).
 *
 * Animation optimisation:
 *   animate() is called every frame but geometry is expensive to allocate.
 *   A param snapshot (built from config.paramOrder) detects whether expression
 *   params actually produced new values. If nothing changed, animate() is a
 *   no-op. This prevents per-frame geometry + mesh allocation that would
 *   exhaust memory — downstream clones (MaterialNode mesh, PerformanceRenderer)
 *   share geometry by reference and old objects are never disposed by the
 *   endpoint chain.
 */

import { BaseNode } from '@nodes/BaseNode';
import { EndpointsRecord, params, ParamKeys, internals } from '@nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three, BufferGeometry } from '../types';
import { geometryConfigs, type GeometryConfig } from './geometryConfigs';

// Base endpoints that all geometry nodes have
const geometryBaseEndpoints = {
  ...internals({ geometryOut: { dataType: 'three:geometry', isOutput: true } })
};

type GeometryBaseEndpoints = typeof geometryBaseEndpoints;

/**
 * Generic geometry node - T = additional endpoints (params) specific to the subclass
 */
class GeometryNode<T extends EndpointsRecord = {}> extends BaseNode<GeometryBaseEndpoints & T> {
  protected three: Three;
  private config: GeometryConfig;
  /** Stored mesh reference from SceneNode — for in-place geometry replacement */
  private mesh: any = null;
  /** Callback to request scene descriptor rebuild (set when part of a pipeline) */
  private rebuildCallback: (() => void) | null = null;
  /** Full param snapshot for change detection in animate() */
  private lastParamSnapshot = '';

  constructor(id: string, config: GeometryConfig, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
    this.config = config;
  }

  initialize(): void {
    super.initialize();
    this.lastParamSnapshot = this.paramSnapshot();
    this.emitGeometry();
  }

  /** Build geometry from current params — called by SceneNode */
  buildGeometry(): BufferGeometry {
    const { threeClass, paramOrder } = this.config;
    const args = paramOrder.map(name => (this.params as any)[name]?.value);
    const GeometryClass = (this.three as Record<string, any>)[threeClass];
    return new GeometryClass(...args);
  }

  /** Store mesh reference from SceneNode for in-place param mutation */
  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  /** Store rebuild callback — when set, param changes trigger a pipeline rebuild
   *  instead of directly mutating the mesh (needed for boolean/merge pipelines). */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  /** Per-frame: rebuild geometry only when expression params produce new values */
  animate(): void {
    const snapshot = this.paramSnapshot();
    if (snapshot === this.lastParamSnapshot) return;
    this.lastParamSnapshot = snapshot;

    if (this.rebuildCallback) {
      this.rebuildCallback();
    } else if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
    this.emitGeometry();
  }

  onParamChanged(): void {
    if (this.rebuildCallback) {
      this.rebuildCallback();
    } else if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
    this.lastParamSnapshot = this.paramSnapshot();
    this.emitGeometry();
  }

  private paramSnapshot(): string {
    return this.config.paramOrder.map(name => (this.params as any)[name]?.value).join(',');
  }

  /** Emit geometry on geometryOut for direct endpoint use */
  private emitGeometry(): void {
    const geo = this.buildGeometry();
    (this.internals as any).geometryOut.set({ value: geo });
  }
}

// ─────────────────────────────────────────────────────────────
// Generate NodeDefs from configs
// ─────────────────────────────────────────────────────────────

export const geometryNodes: NodeDef[] = geometryConfigs.map(config => {
  const configParams = params(config.params);
  const endpoints = {
    ...configParams,
    ...geometryBaseEndpoints
  };

  return {
    type: config.type,
    shortName: config.shortName,
    label: config.label,
    category: 'geometry',
    endpoints,
    ctor: class extends GeometryNode<typeof configParams> {
      constructor(id: string, deps: ThreeDependencies) {
        super(id, config, deps);
      }
    }
  };
});
